import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select } from 'antd';
import { 
  BinaryFileSettings,
  DropdownSettings,
  MultilineTextSettings,
  NumberControlSettings,
  SliderSettings,
  TextSettings,
  ImageSettings,
  CheckboxSettings,
  ValidControlSettings,
  MultiSelectSettings,
  GroupSettings,
  ButtonSettings,
} from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { DataType, GlobalVariableControlType } from '@kemu-io/hs-types';
import globalVariablesManager, { parsePath } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { useTranslation } from '@hooks/index';
import RoundedModal from '@src/components/roundedModal/roundedModal';
import styles from './AddVariableModal.module.css';

type Props = {
  visible: boolean;
  onClose: () => void;
}

const defaultControlSettings: Record<GlobalVariableControlType, ValidControlSettings> = {
  number: { value: 0 } as NumberControlSettings,
  slider: { min: 0, max: 100, step: 1 } as SliderSettings,
  text: { value: '', placeholder: 'Enter text' } as TextSettings,
  multilineText: { value: '', placeholder: 'Enter text...' } as MultilineTextSettings,
  dropdown: { options: ['Option 1', 'Option 2'], value: 'Option 1' } as DropdownSettings,
  image: { value: null } as ImageSettings,
  binaryFile: { value: null } as BinaryFileSettings,
  checkbox: { value: false } as CheckboxSettings  ,
  multiSelect: { options: ['Option 1', 'Option 2'], value: [] } as MultiSelectSettings,
  anything: { value: null } as ValidControlSettings,
  group: { value: null } as GroupSettings,
  button: { value: null } as ButtonSettings,
};
/**
 * Modal for adding new global variables
 * @param visible - Whether the modal is visible
 * @param onClose - Callback when modal should be closed
 */
const AddVariableModal = ({ visible, onClose }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const t = useTranslation('GlobalVariablesPanel');

  const controlTypeOptions = [
    { label: t('ControlTypes.Anything'), value: 'anything' as GlobalVariableControlType },
    { label: t('ControlTypes.Number'), value: 'number' as GlobalVariableControlType },
    { label: t('ControlTypes.Slider'), value: 'slider' as GlobalVariableControlType },
    { label: t('ControlTypes.Text'), value: 'text' as GlobalVariableControlType },
    { label: t('ControlTypes.MultilineText'), value: 'multilineText' as GlobalVariableControlType },
    { label: t('ControlTypes.Dropdown'), value: 'dropdown' as GlobalVariableControlType },
    { label: t('ControlTypes.Image'), value: 'image' as GlobalVariableControlType },
    { label: t('ControlTypes.BinaryFile'), value: 'binaryFile' as GlobalVariableControlType },
    { label: t('ControlTypes.Checkbox'), value: 'checkbox' as GlobalVariableControlType },
    { label: t('ControlTypes.MultiSelect'), value: 'multiSelect' as GlobalVariableControlType },
    { label: t('ControlTypes.Button'), value: 'button' as GlobalVariableControlType },
    { label: t('ControlTypes.Group'), value: 'group' as GlobalVariableControlType },
  ];

  /**
   * Gets all available groups for the dropdown
   */
  const availableGroups = useMemo(() => {
    const allVariables = globalVariablesManager.getAllVariables();
    return allVariables
      .filter(v => globalVariablesManager.isGroupVariable(v))
      .sort((a, b) => {
        const aOrder = a.order ?? Infinity;
        const bOrder = b.order ?? Infinity;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.name.localeCompare(b.name);
      })
      .map(v => {
        // Extract display name from full path for label, but use full path as value
        const { name: displayName } = parsePath(v.name);
        return { label: displayName, value: v.name };
      });
  }, [visible]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Create default control settings based on control type
      let controlSettings = {};
      let dataType = DataType.String;
      
      switch (values.controlType) {
        case 'number':
          controlSettings = { value: 0 };
          dataType = DataType.Number;
          break;
        case 'slider':
          controlSettings = defaultControlSettings.slider;
          dataType = DataType.Number;
          break;
        case 'text':
          controlSettings = defaultControlSettings.text;
          dataType = DataType.String;
          break;
        case 'multilineText':
          controlSettings = defaultControlSettings.multilineText;
          dataType = DataType.String;
          break;
        case 'dropdown':
          controlSettings = defaultControlSettings.dropdown;
          dataType = DataType.String;
          break;
        case 'image':
          controlSettings = defaultControlSettings.image;
          dataType = DataType.ImageData;
          break;
        case 'binaryFile':
          controlSettings = defaultControlSettings.binaryFile;
          dataType = DataType.BinaryFile;
          break;
        case 'checkbox':
          controlSettings = defaultControlSettings.checkbox;
          dataType = DataType.Boolean;
          break;
        case 'multiSelect':
          controlSettings = defaultControlSettings.multiSelect;
          dataType = DataType.Array;
          break;
        case 'anything':
          controlSettings = defaultControlSettings.anything;
          dataType = DataType.Anything;
          break;
        case 'group':
          controlSettings = defaultControlSettings.group;
          dataType = DataType.Anything;
          break;
      }

      globalVariablesManager.setGlobalVariable({
        name: values.name,
        value: null,
        lastDataType: dataType,
        controlType: values.controlType,
        helpText: values.helpText,
        controlSettings: controlSettings as ValidControlSettings,
        group: values.group || null,
      });

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Error creating variable:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <RoundedModal
      title={t('AddVariableModal.Title')}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      footerClassName={styles.Footer}
      okBtnLabel={t.withBaseKey('CommonWords')('Create')}
      cancelBtnLabel={t.withBaseKey('CommonWords')('Cancel')}
      loadingOkBtn={loading}
      width={500}
    >
      <div className={styles.Container}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label={t('AddVariableModal.VariableName')}
            rules={[
              { required: true, message: t('AddVariableModal.NameRequired') },
              { 
                pattern: /^(?!.*-)[a-zA-Z0-9_\s]+$/, 
                message: t('AddVariableModal.NameInvalid') 
              },
              {
                validator: (_, value) => {
                  if (value && value.includes('.')) {
                    return Promise.reject(new Error('Variable names cannot contain dots'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t('AddVariableModal.NamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="controlType"
            label={t('AddVariableModal.ControlType')}
            rules={[{ required: true, message: t('AddVariableModal.ControlTypeRequired') }]}
          >
            <Select 
              placeholder={t('AddVariableModal.ControlTypePlaceholder')}
              options={controlTypeOptions}
              popupClassName={styles.SelectDropdown}
            />
          </Form.Item>

          {availableGroups.length > 0 && (
            <Form.Item
              name="group"
              label={t('AddVariableModal.Group')}
            >
              <Select 
                placeholder={t('AddVariableModal.GroupPlaceholder')}
                options={availableGroups}
                allowClear
                popupClassName={styles.SelectDropdown}
              />
            </Form.Item>
          )}

          <Form.Item
            name="helpText"
            label={t('AddVariableModal.HelpText')}
          >
            <Input.TextArea 
              placeholder={t('AddVariableModal.HelpTextPlaceholder')}
              rows={3}
            />
          </Form.Item>
        </Form>
      </div>
    </RoundedModal>
  );
};

export default AddVariableModal;
